import type { NavigationGuardWithThis } from 'vue-router'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useDatapoints } from '@/composables/useDatapoints'

/**
 * Datapoint-based route access. Keeps direct-URL navigation in step with the sidebar / quick-action
 * gating so a hidden link can't be reached by typing the URL:
 * - `meta.blockedByDatapoint` — redirect to the dashboard while a "hide/restrict" datapoint is present
 *   (mirrors Vue 2, which redirects People Search server-side on HIDE_PEOPLE_SEARCH).
 * - `meta.requiresDatapoint` — redirect to the dashboard unless an "enable" datapoint is present
 *   (e.g. AI Assistant + ENABLE_AI_ASSISTANT; the sidebar only hid the link, leaving the route open).
 * Runs after `authGuard`, so it only sees authenticated navigations.
 */
export const datapointGuard: NavigationGuardWithThis<undefined> = async (to) => {
  const blocker = to.meta.blockedByDatapoint
  const required = to.meta.requiresDatapoint
  if ((!blocker && !required) || to.meta.requiresAuth === false) return true

  const auth = useAuthStore()
  if (!auth.isAuthenticated) return true // authGuard has already redirected to login

  const { hidePeopleSearch, enableAiAssistant, fetchDatapoints } = useDatapoints()
  await fetchDatapoints()

  // Map each blocker code to its (true = restricted) datapoint flag.
  if (blocker) {
    const isBlocked: Record<NonNullable<typeof blocker>, boolean> = {
      HIDE_PEOPLE_SEARCH: hidePeopleSearch.value,
    }
    if (isBlocked[blocker]) return { name: 'Dashboard' }
  }

  // Map each required code to its (true = enabled) datapoint flag; absent → redirect away.
  if (required) {
    const isEnabled: Record<NonNullable<typeof required>, boolean> = {
      ENABLE_AI_ASSISTANT: enableAiAssistant.value,
    }
    if (!isEnabled[required]) return { name: 'Dashboard' }
  }

  return true
}
